import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink, Phone } from "lucide-react";
// CategoryNav removed
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const KIDS_TYPES = ["Tutoring", "Learning Centers", "Day Care", "Music", "Party Venues", "Play House", "Face Painting"] as const;

const KIDS_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "type", label: "Type", placeholder: "Tutoring, Learning Centers, Day Care, Music, Party Venues, Play House" },
  { key: "city", label: "City", placeholder: "Frisco" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface KidsEntry {
  name: string;
  type: string;
  city: string;
  address: string;
  image: string;
  instagram?: string;
  website?: string;
  phone?: string;
  title?: string;
  facebook?: string;
  youtube?: string;
  tiktok?: string;
  detailDescription?: string;
  // When true, social links and the Call button are hidden on grid cards
  // (Kids page / search results) but still shown on the individual detail page.
  hideCardActions?: boolean;
}

// Cards featured by TastebudsByAnubhi — matched by name so it survives
// managed-collection re-saves the same way Restaurants/Desserts/Grocery do.
const FEATURED_KIDS_NAMES = new Set(["wonder kids", "wonder kids frisco"]);
function isFeaturedKid(e: { name?: string }) {
  return FEATURED_KIDS_NAMES.has(String(e?.name || "").trim().toLowerCase());
}

export const KIDS_ENTRIES: KidsEntry[] = [
  {
    name: "Inspiration Masters",
    type: "Learning Centers",
    title: "Communication Leadership Institute",
    city: "Dallas",
    address: "",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-22/9d4cc0ca2340.jpeg",
    instagram: "https://www.instagram.com/p/DV2BqJBmnd9/",
    phone: "972-974-7573",
  },
  {
    name: "KidZania Dallas",
    type: "Play House, Party Venues",
    city: "Frisco",
    address: "2601 Preston Rd #3011, Frisco, TX 75034",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-13/01a4d2df6df6.jpeg",
    website: "https://dallas.kidzaniausa.com",
  } as any,
  {
    name: "Wonder Kids Frisco",
    type: "Play House, Party Venues",
    city: "Frisco",
    address: "13259 Main St, Frisco, TX",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784563019603-bugae7.png",
    instagram: "https://www.instagram.com/wonderkidshq/",
  },
  {
    name: "Prosper Playhouse",
    type: "Play House",
    city: "Prosper",
    address: "550 Industry Way Ste 10, Prosper, TX 75078",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-24/ad8f2905dfc6.jpeg",
    instagram: "https://www.instagram.com/prosperplayhouse1/",
    phone: "972-292-8474",
    hideCardActions: true,
    detailDescription:
      "Prosper Playhouse is a locally owner indoor playhouse and children's birthday party venue designed for kids to play, learn, explore, and make lasting memories. From exciting themed birthday party celebrations to Parents' Night Out Fridays to fun summer day camps, we offer fun-filled experiences for kids and a little extra flexibility for parents. We have imaginative play activities, special events, and community connection opportunities. We pride ourselves on creating a welcoming, inclusive, and fun space where every child can find a sense of belonging. At Prosper Playhouse, we grow happy minds, create unforgettable memories, and strengthen our community one child at a time!",
  },
  {
    name: "Pinnacle Montessori",
    type: "Day Care",
    city: "Frisco",
    address: "10247 Warren Pkwy, Frisco, TX 75035",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784574829082-wimtre.jpg",
  },
  {
    name: "Eye Level Castle Hills",
    type: "Learning Centers",
    city: "Carrollton",
    address: "1616 W Hebron Pkwy #116, Carrollton, TX 75010",
    image: "",
  },
  {
    name: "Eye Level Learning Center of Colleyville",
    type: "Learning Centers",
    city: "Colleyville",
    address: "5615 Colleyville Blvd Suite 460, Colleyville, TX 76034",
    image: "",
  },
  {
    name: "Best Brains Learning Center - Frisco",
    type: "Learning Centers",
    city: "Frisco",
    address: "2575 Main St #325, Frisco, TX 75034",
    image: "",
  },
  {
    name: "Best Brains Learning Center - Plano",
    type: "Learning Centers",
    city: "Plano",
    address: "2865 McDermott Rd #180, Plano, TX 75025",
    image: "",
  },
  {
    name: "Richwoods Montessori Academy",
    type: "Day Care, Learning Centers",
    city: "Frisco",
    address: "4633 Coit Rd Ste #310, Frisco, TX 75035",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783742898394-lpzyan.png",
  },
  {
    name: "Stacy Montessori Academy",
    type: "Day Care, Learning Centers",
    city: "McKinney",
    address: "5400 Stacy Rd, McKinney, TX 75070",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-12/eb4052624139.jpeg",
  },
  {
    name: "Christopher",
    type: "Face Painting",
    city: "",
    address: "",
    image: "",
    phone: "(214) 475-8984",
    title: "Face Painter",
  },
  {
    name: "Joana",
    type: "Face Painting",
    city: "",
    address: "",
    image: "",
    phone: "+1 (469) 882-7106",
    title: "Face Painter",
  },
  {
    name: "Hands-On Learning Montessori Home Daycare",
    type: "Day Care",
    city: "Frisco",
    address: "Ignatius Dr, Frisco, TX 75035",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-18/1b438a39d7eb.jpeg",
    phone: "(214) 903-9119",
  },
  {
    name: "Smart Math Tutoring",
    type: "Tutoring",
    city: "Plano",
    address: "5805 Coit Rd Ste 403, Plano, TX 75093",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784557236847-dtzuze.png",
  },
  {
    name: "JMstudiocreativo",
    type: "Party Venues",
    city: "",
    address: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784572301279-cwrtx.png",
    title: "Luxury Kids Party Design",
    instagram: "https://www.instagram.com/jmstudiocreativoo/",
  } as any,
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=600&h=400&fit=crop";

function mapsUrl(e: KidsEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

// Same key function the Kids grid uses for its managed collection, so the
// slug in the URL always matches the entry the grid card linked from.
export function kidsSlug(e: any, i: number) {
  return slugify(e.name || String(i));
}

export function KidsCard({ entry, index, isFeatured, href }: { entry: KidsEntry; index: number; isFeatured?: boolean; href?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full aspect-square relative overflow-hidden">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
        {entry.type && (
          <span className="absolute top-1 left-1 bg-white/90 text-[8px] font-semibold text-gray-700 px-1.5 py-0.5 rounded-full shadow-sm">
            {entry.type}
          </span>
        )}
      </div>
      <div className="p-1.5 pb-0.5 flex-1">
        <h3 className="text-[10px] font-bold text-gray-900 leading-tight truncate">
          {href ? (
            <a href={href} className="hover:text-[#082f6d] hover:underline">
              {entry.name}
            </a>
          ) : (
            entry.name
          )}
        </h3>
        {entry.title && (
          <p className="text-[9px] text-gray-500 font-medium leading-snug truncate">
            {entry.title}
          </p>
        )}
        {entry.address && (
          <div className="flex items-start gap-0.5">
            <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
            <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
              {entry.address}
            </span>
          </div>
        )}
        {isFeatured && (
          <div className="pt-1 flex items-center gap-1">
            <p className="text-[8px] font-semibold text-green-600 tracking-wide truncate">
              Featured by TastebudsByAnubhi
            </p>
            <SocialLinks item={entry} px={16} />
          </div>
        )}
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        {!isFeatured && !entry.hideCardActions && (
          entry.instagram && !entry.website && !entry.facebook && !entry.youtube && !entry.tiktok ? (
            <SocialLinks item={entry} variant="sleek" />
          ) : (
            <SocialLinks item={entry.website ? { ...entry, website: "" } : entry} px={20} />
          )
        )}
        {entry.website && (
          <a
            href={/^https?:\/\//i.test(entry.website) ? entry.website : `https://${entry.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" /> Website
          </a>
        )}
        {entry.address && (
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-[#f97316] hover:bg-[#ea580c] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
        {entry.phone && !entry.hideCardActions && (
          <a
            href={`tel:${entry.phone.replace(/[^\d+]/g, "")}`}
            className="flex items-center justify-center gap-0.5 bg-[#51ba4f] hover:bg-[#45a943] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200"
          >
            <Phone className="w-2.5 h-2.5" /> Call
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Kids() {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const mc = useManagedCollection<any>("kids", KIDS_ENTRIES, kidsSlug);

  const filtered = mc.items.filter(
    (e) => {
      const matchesQuery =
        query.trim() === "" ||
        e.name.toLowerCase().includes(query.trim().toLowerCase());
      // Normalize plural/singular and the old "Play Area(s)" label so any
      // stale saved override still matches the current "Play House" chip.
      const norm = (s: string) => {
        const t = s.trim().toLowerCase().replace(/s$/, "");
        return t === "play area" ? "play house" : t;
      };
      const matchesType =
        !selectedType ||
        String(e.type || "")
          .split(",")
          .map((t: string) => norm(t))
          .includes(norm(selectedType));
      return matchesQuery && matchesType;
    }
  );

  const featuredKids = filtered.filter((e) => isFeaturedKid(e));
  const nonFeaturedKids = filtered
    .filter((e) => !isFeaturedKid(e))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

  const hasActiveFilter = !!selectedType;

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        placeholder="Search kids…"
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-18/9fa0ded64502.jpeg"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      {/* CategoryNav removed */}

      {/* ── Type Filter Chips ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-5 pb-3">
          <div className="flex items-center justify-between mb-3">
            <p
              className="text-[11px] font-bold text-gray-400 uppercase"
              style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: "0.12em" }}
            >
              Explore by Type
            </p>
            {hasActiveFilter && (
              <button
                onClick={() => setSelectedType(null)}
                className="shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold text-gray-500 border border-dashed border-gray-300 hover:text-[#f97316] hover:border-[#f97316] hover:bg-orange-50 transition-all duration-200"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                ✕ Clear Filters
              </button>
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
            {KIDS_TYPES.map((type) => {
              const isActive = selectedType === type;
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(isActive ? null : type)}
                  className={`shrink-0 px-4 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 ${
                    isActive
                      ? "bg-[#f97316] text-white shadow-sm"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {type}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Dallas Little Scoop
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              {selectedType
                ? `${selectedType} for kids — tap "View Location" for directions.`
                : `Learning centers and activities for kids — tap "View Location" for directions.`}
            </p>
            <AdminAddBar mc={mc} fields={KIDS_FIELDS} label="Add Kids Listing" />

            {/* Featured by TastebudsByAnubhi */}
            {featuredKids.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Featured by TastebudsByAnubhi
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                  {featuredKids.map((e, i) => (
                    <AdminItem key={e._key} mc={mc} item={e} fields={KIDS_FIELDS} title="Kids Listing" showResetToDefault>
                      <KidsCard entry={e as any} index={i} isFeatured href={e.name ? `/kids/${kidsSlug(e, i)}` : undefined} />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {/* More Kids */}
            {nonFeaturedKids.length > 0 ? (
              <div>
                {featuredKids.length > 0 && (
                  <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Explore More for Kids
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                  {nonFeaturedKids.map((e, i) => (
                    <AdminItem key={e._key} mc={mc} item={e} fields={KIDS_FIELDS} title="Kids Listing" showResetToDefault>
                      <KidsCard entry={e as any} index={i} href={e.name ? `/kids/${kidsSlug(e, i)}` : undefined} />
                    </AdminItem>
                  ))}
                </div>
              </div>
            ) : featuredKids.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No kids listings match your search.
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
